<template>
	<div class="welcome-back-popup" :data-deeplink-type="deepLinkType">
		<div class="welcome-back-popup__header">
			<div class="welcome-back-popup__header-bg">
				<div class="welcome-back-popup__header-bg-img" />
			</div>

			<div class="welcome-back-popup__header-heading">
				<div class="heading-wrapper">
					<ShrinkText
						:text="translate('deeplink.welcome_back.heading')"
						:fontSize="20"
						textClassList="welcome-back-popup__header-ribbon-text"
					/>
				</div>
				<div class="welcome-back-popup__header-ribbon" />
			</div>
		</div>

		<div class="welcome-back-popup__content-wrapper">
			<div class="welcome-back-popup__content">
				<div v-if="messageText" class="welcome-back__message">
					{{ translate(messageText) }}
				</div>

				<div
					v-if="
						deepLinkType === DeepLinkType.WELCOME_BACK_REWARD &&
						welcomeBackReward
					"
					class="welcome-back__reward"
				>
					<OneSlotOfferCardDetail
						v-if="
							welcomeBackReward.attributes.template ===
							OfferTemplate.SLOTS_1
						"
						:contentData="welcomeBackReward"
					/>

					<TwoSlotOfferCardDetail
						v-if="
							welcomeBackReward.attributes.template ===
							OfferTemplate.SLOTS_2
						"
						:contentData="welcomeBackReward"
					/>

					<ThreeSlotOfferCardDetail
						v-if="
							welcomeBackReward.attributes.template ===
							OfferTemplate.SLOTS_3
						"
						:contentData="welcomeBackReward"
					/>

					<FourSlotOfferCardDetail
						v-if="
							welcomeBackReward.attributes.template ===
							OfferTemplate.SLOTS_4
						"
						:contentData="welcomeBackReward"
					/>
				</div>

				<div class="welcome-back__actions">
					<AppTextButton
						v-if="showClaimButton"
						:type="StatusTypes.SUCCESS"
						:text="translate('button.claim')"
						@click="handleClaim"
					/>
					<AppTextButton
						v-else
						class="close-btn"
						:text="translate('button.continue')"
						:type="StatusTypes.PRIMARY"
						@click="close"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import FourSlotOfferCardDetail from "@/components/shop/templates/FourSlotOfferCardDetail.vue";
import OneSlotOfferCardDetail from "@/components/shop/templates/OneSlotOfferCardDetail.vue";
import ThreeSlotOfferCardDetail from "@/components/shop/templates/ThreeSlotOfferCardDetail.vue";
import TwoSlotOfferCardDetail from "@/components/shop/templates/TwoSlotOfferCardDetail.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import type { ClaimablePack } from "@/libs/SocketService";
import { usePopupStore } from "@/store/usePopupStore";
import useShopStore from "@/store/useShopStore";
import useUserStore from "@/store/useUserStore";
import { OfferTemplate } from "@/types/shop";
import { StatusTypes } from "@/types/ui";
import { DeepLinkType } from "@/utils/deepLinkHandler";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	deepLinkType?: DeepLinkType | null;
	showClaimButton?: boolean;
	storeId?: string;
}

const {
	deepLinkType = null,
	showClaimButton = false,
	storeId,
} = defineProps<Props>();

const popupStore = usePopupStore();
const shopStore = useShopStore();
const userStore = useUserStore();

const { welcomeBackReward } = storeToRefs(shopStore);

const messageTemplates: Record<string, string> = {
	[DeepLinkType.WELCOME_BACK]: "deeplink.welcome_back.main",
	[DeepLinkType.WELCOME_BACK_REWARD]: "deeplink.welcome_back.reward",
	[DeepLinkType.WELCOME_BACK_OFFER]: "deeplink.welcome_back.offer",
};

const close = (): void => {
	popupStore.hidePopup();
};

const messageText = computed((): string | null => {
	if (!deepLinkType) return null;

	return (
		messageTemplates[deepLinkType] ??
		messageTemplates[DeepLinkType.WELCOME_BACK]
	);
});

const handleClaim = (): void => {
	if (!storeId) return;

	close();

	const pack = welcomeBackReward.value;

	if (pack) {
		window.purchaseFinished(
			storeId,
			"true",
			[
				{
					_id: pack.store_id ?? "",
					pack: pack as unknown as ClaimablePack,
				},
			],
			true,
		);
	}

	// Mark as claimed so subsequent deep links show the standard welcome-back
	if (deepLinkType === DeepLinkType.WELCOME_BACK_REWARD) {
		userStore.setWelcomeBackRewardClaimed(true);
	}
};
</script>

<style lang="scss" scoped>
.welcome-back {
	&-popup {
		position: relative;
		border-radius: 0.15rem;
		background-image: linear-gradient(to bottom, #d7bd8d, #f2dcb5);
		padding: 0.125rem;
		width: 100%;
		@include flex-col-start;
		min-height: 10rem;

		&__header {
			position: relative;
			z-index: 1;
			width: 100%;
			height: 11.625rem;

			&-bg {
				display: flex;
				position: absolute;
				top: 0;
				left: 0;
				flex-direction: column;
				justify-content: center;
				align-items: center;
				width: 100%;
				height: 100%;
				overflow: hidden;
				overflow-x: clip;
				overflow-y: visible;

				&-img {
					width: 100%;
					height: 100%;
				}
			}

			&-heading {
				position: absolute;
				bottom: -1.75rem;
				left: 50%;
				transform: translateX(-50%);
			}

			&-ribbon {
				position: relative;
				filter: drop-shadow(0 0.5rem 0.75rem rgba(0, 0, 0, 0.8));
				width: 25.625rem;
				height: 3.875rem;
				@include background(
					url($path-images + "deeplink/welcome-back/backgrounds/welcome-back-popup-header-ribbon.avif"),
					contain,
					center,
					no-repeat
				);
			}

			.heading-wrapper {
				position: absolute;
				top: calc(50% + 0.25rem);
				left: 50%;
				transform: translate(-50%, -50%);
				z-index: 1;
				width: 90%;

				:deep(.text-container) {
					.welcome-back-popup__header-ribbon-text {
						color: #f4d278;
						font-weight: 800;
						text-align: center;
						text-shadow: 0.044rem 0.044rem 0.063rem
							rgba(0, 0, 0, 0.6);
						text-transform: uppercase;
					}
				}
			}
		}

		&__content {
			position: relative;
			z-index: 1;
			padding: 2rem 0.5rem 1.25rem;
			width: 100%;
			height: 100%;
			@include flex-col-between;
			flex: 1 1 auto;

			&-wrapper {
				position: relative;
				padding-top: 2rem;
				width: 100%;
				min-height: 10rem;
				@include flex-col-center;

				&::before {
					position: absolute;
					top: 0;
					left: 0;
					opacity: 1;
					mix-blend-mode: multiply;
					background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
						repeat center;
					background-size: 40%;
					width: 100%;
					height: 100%;
					content: "";
				}
			}
		}

		&[data-deeplink-type="welcome-back"] {
			.welcome-back-popup__header-bg-img {
				@include background(
					url($path-images + "deeplink/welcome-back/backgrounds/welcome-back-popup-header-bg.avif"),
					cover,
					center,
					no-repeat
				);
			}

			.welcome-back-popup__content-wrapper {
				background-image: linear-gradient(to top, #d7bd8d, #f2dcb5);
			}

			.welcome-back-popup__content {
				gap: 2.5rem;
			}

			.welcome-back__message {
				color: #372615;
			}
		}

		&[data-deeplink-type="welcome-back-reward"] {
			.welcome-back-popup__header-bg-img {
				@include background(
					url($path-images + "deeplink/welcome-back/backgrounds/welcome-back-reward-popup-header-bg.avif"),
					cover,
					center,
					no-repeat
				);
			}

			.welcome-back-popup__content-wrapper {
				background: linear-gradient(
					0deg,
					rgba(19, 70, 85, 1) 0%,
					rgba(63, 154, 180, 1) 100%
				);
			}

			.welcome-back__message {
				color: #fff3dd;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
		}
	}

	&__message {
		padding: 0 2rem;
		color: #372615;
		font-size: 0.875rem;
		line-height: 1.5;
		text-align: center;
	}

	&__actions {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.5rem;
		width: 100%;
	}

	&__reward {
		width: 100%;
	}
}
</style>
